import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, BellOff, BellRing, X } from "lucide-react";
import { t, type SupportedLocale } from "@/lib/i18n";
import styles from "@/styles/PushSubscribe.module.css";
type PushStatus = "loading" | "unsupported" | "ios" | "denied" | "off" | "on";
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}
function isIosDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const iPadOs = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOS || iPadOs;
}
function isStandaloneDisplay(): boolean {
  if (typeof window === "undefined") return false;
  const nav = navigator as Navigator & {
    standalone?: boolean;
  };
  return window.matchMedia("(display-mode: standalone)").matches || nav.standalone === true;
}
function pushSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;
}
async function getExistingSubscription(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.getRegistration("/");
  if (!registration) return null;
  return registration.pushManager.getSubscription();
}
const PROMPT_KEY = "12go_push_prompt";
const PROMPT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
const PROMPT_DELAY_MS = 10_000;
function alreadyPromptedRecently(): boolean {
  try {
    if (sessionStorage.getItem(PROMPT_KEY)) return true;
    const raw = localStorage.getItem(PROMPT_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return true;
    return Date.now() - ts < PROMPT_COOLDOWN_MS;
  } catch {
    return true;
  }
}
function markPromptSeen(persistent: boolean): void {
  try {
    sessionStorage.setItem(PROMPT_KEY, "1");
    if (persistent) localStorage.setItem(PROMPT_KEY, String(Date.now()));
  } catch {
    /* private mode */
  }
}
function isNearPageEnd(): boolean {
  const doc = document.documentElement;
  const scrollable = doc.scrollHeight - window.innerHeight;
  if (scrollable < 160) return false;
  return window.scrollY + window.innerHeight >= doc.scrollHeight - 80;
}
export function PushSubscribeButton({
  locale
}: {
  locale: SupportedLocale;
}) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<PushStatus>("loading");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [autoReady, setAutoReady] = useState(false);
  const [mounted, setMounted] = useState(false);
  const autoReadyRef = useRef(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  const closeDialog = (snooze: boolean) => {
    setOpen(false);
    markPromptSeen(snooze);
  };
  const qualifyAutoPrompt = () => {
    if (autoReadyRef.current) return;
    autoReadyRef.current = true;
    setAutoReady(true);
  };
  useEffect(() => {
    let cancelled = false;
    async function refresh() {
      if (!pushSupported()) {
        if (isIosDevice() && !isStandaloneDisplay()) {
          if (!cancelled) setStatus("ios");
          return;
        }
        if (!cancelled) setStatus("unsupported");
        return;
      }
      if (Notification.permission === "denied") {
        if (!cancelled) setStatus("denied");
        return;
      }
      const subscription = await getExistingSubscription();
      if (!cancelled) setStatus(subscription ? "on" : "off");
    }
    void refresh();
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeDialog(true);
    };
    window.addEventListener("keydown", onKeyDown);
    dialogRef.current?.querySelector("button")?.focus();
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);
  useEffect(() => {
    if (alreadyPromptedRecently()) return;
    let remaining = PROMPT_DELAY_MS;
    let lastTick = Date.now();
    let timer: number | undefined;
    const armTimer = () => {
      window.clearTimeout(timer);
      if (document.hidden || autoReadyRef.current) return;
      lastTick = Date.now();
      timer = window.setTimeout(qualifyAutoPrompt, remaining);
    };
    const onVisibility = () => {
      if (document.hidden) {
        remaining = Math.max(0, remaining - (Date.now() - lastTick));
        window.clearTimeout(timer);
        return;
      }
      armTimer();
    };
    const onScroll = () => {
      if (isNearPageEnd()) qualifyAutoPrompt();
    };
    armTimer();
    onScroll();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);
  useEffect(() => {
    if (!autoReady || open || status !== "off") return;
    if (alreadyPromptedRecently()) return;
    markPromptSeen(false);
    setMessage(null);
    setOpen(true);
  }, [autoReady, open, status]);
  const enable = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const keyRes = await fetch("/api/push/vapid-public-key");
      if (!keyRes.ok) throw new Error("vapid");
      const {
        publicKey
      } = (await keyRes.json()) as {
        publicKey: string;
      };
      const registration = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
        updateViaCache: "none"
      });
      await navigator.serviceWorker.ready;
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setStatus(permission === "denied" ? "denied" : "off");
        setMessage(permission === "denied" ? t(locale, "push.denied") : null);
        if (permission === "denied") markPromptSeen(true);
        return;
      }
      const subscription_0 = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource
      });
      const raw = subscription_0.toJSON();
      const res = await fetch("/api/push/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          endpoint: raw.endpoint,
          keys: raw.keys,
          locale
        })
      });
      if (!res.ok) throw new Error("subscribe");
      setStatus("on");
      setMessage(t(locale, "push.enabled"));
      markPromptSeen(true);
    } catch {
      setMessage(t(locale, "push.error"));
    } finally {
      setBusy(false);
    }
  };
  const disable = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const subscription_1 = await getExistingSubscription();
      if (subscription_1) {
        await fetch("/api/push/unsubscribe", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            endpoint: subscription_1.endpoint
          })
        });
        await subscription_1.unsubscribe();
      }
      setStatus("off");
      setMessage(t(locale, "push.disabled"));
    } catch {
      setMessage(t(locale, "push.error"));
    } finally {
      setBusy(false);
    }
  };
  const label = status === "on" ? t(locale, "push.buttonOn") : t(locale, "push.button");
  const Icon = status === "on" ? BellRing : status === "denied" ? BellOff : Bell;
  return <>
      <button type="button" className={styles.trigger} onClick={() => {
      setMessage(null);
      setOpen(true);
    }} aria-haspopup="dialog" aria-expanded={open} title={label}>
        <Icon size={18} aria-hidden />
        <span className={styles.triggerLabel}>{label}</span>
      </button>

      {mounted && open && createPortal(<div className={styles.backdrop} onClick={() => closeDialog(true)}>
          <div className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby={titleId} ref={dialogRef} onClick={event_0 => event_0.stopPropagation()}>
            <div className={styles.dialogHead}>
              <h2 id={titleId} className={styles.dialogTitle}>
                {t(locale, "push.dialogTitle")}
              </h2>
              <button type="button" className={styles.close} onClick={() => closeDialog(true)} aria-label={t(locale, "push.dialogClose")}>
                <X size={18} aria-hidden />
              </button>
            </div>
            <p className={styles.dialogBody}>{t(locale, "push.dialogBody")}</p>
            <p className={styles.dialogPrivacy}>{t(locale, "push.dialogPrivacy")}</p>
            {status === "unsupported" && <p className={styles.note}>{t(locale, "push.unsupported")}</p>}
            {status === "ios" && <p className={styles.note}>{t(locale, "push.iosHint")}</p>}
            {status === "denied" && <p className={styles.note}>{t(locale, "push.denied")}</p>}
            {message && <p className={styles.note}>{message}</p>}
            <div className={styles.actions}>
              {status === "on" ? <button type="button" className={styles.secondary} onClick={() => void disable()} disabled={busy}>
                  {t(locale, "push.buttonOff")}
                </button> : status === "off" ? <button type="button" className={styles.primary} onClick={() => void enable()} disabled={busy}>
                  {t(locale, "push.dialogConfirm")}
                </button> : null}
              <button type="button" className={styles.ghost} onClick={() => closeDialog(true)}>
                {t(locale, "push.dialogCancel")}
              </button>
            </div>
          </div>
        </div>, document.body)}
    </>;
}